import React, { useState, useEffect, useCallback } from "react";
import { Plus, Check, History, IndianRupee } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const cls = "w-28 rounded-lg border border-[#2A1B1D]/12 bg-white px-3 py-2 text-sm outline-none focus:border-[#D4AF37]";

export default function PriceManager({ services, reload, onAdd }) {
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const init = {};
    services.forEach((s) => {
      init[s.id] = { price: s.price, discount_price: s.discount_price ?? "" };
    });
    setRows(init);
  }, [services]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/price-history");
      setHistory(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const setField = (id, key, value) => setRows((r) => ({ ...r, [id]: { ...r[id], [key]: value } }));

  const dirty = (s) => {
    const r = rows[s.id];
    if (!r) return false;
    const disc = r.discount_price === "" ? null : Number(r.discount_price);
    return Number(r.price) !== s.price || disc !== (s.discount_price ?? null);
  };

  const buildPayload = (s, overrides = {}) => ({
    name: s.name,
    category: s.category,
    description: s.description || "",
    image: s.image || "",
    duration: s.duration ?? 60,
    price: s.price,
    discount_price: s.discount_price ?? null,
    status: s.status,
    featured: s.featured ?? false,
    popular: s.popular ?? false,
    order: s.order ?? 0,
    ...overrides,
  });

  const savePrice = async (s) => {
    const r = rows[s.id];
    const price = Number(r.price);
    if (!price || price <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    const discount_price = r.discount_price === "" || r.discount_price == null ? null : Number(r.discount_price);
    if (discount_price != null && discount_price >= price) {
      toast.error("Discount price must be lower than the regular price");
      return;
    }
    setSaving(s.id);
    try {
      await api.put(`/services/${s.id}`, buildPayload(s, { price, discount_price }));
      toast.success(`${s.name} price updated to ${inr(price)}`);
      await reload();
      await loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(null);
    }
  };

  const toggleStatus = async (s) => {
    try {
      await api.put(`/services/${s.id}`, buildPayload(s, { status: !s.status }));
      toast.success(`${s.name} ${!s.status ? "enabled" : "disabled"}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl text-[#2A1B1D]">Service Prices</h1>
          <p className="text-sm text-[#5C4A4D] mt-1">Update prices anytime — changes appear on the website instantly.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowHistory((v) => !v)} data-testid="toggle-price-history" className="inline-flex items-center gap-2 rounded-full border border-[#2A1B1D]/15 px-5 py-3 text-sm font-medium text-[#2A1B1D] hover:border-[#8E3B46] hover:text-[#8E3B46] transition-colors">
            <History size={16} /> Price History
          </button>
          <button onClick={onAdd} data-testid="price-add-service-btn" className="inline-flex items-center gap-2 rounded-full bg-[#8E3B46] px-5 py-3 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors">
            <Plus size={16} /> Add Service
          </button>
        </div>
      </div>

      <div className="mt-8 rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#5C4A4D] bg-[#F4EFEA]/60">
              <th className="px-5 py-3">Service</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Regular Price (₹)</th>
              <th className="px-5 py-3">Discount Price (₹)</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Save</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => {
              const r = rows[s.id] || { price: s.price, discount_price: s.discount_price ?? "" };
              const changed = dirty(s);
              return (
                <tr key={s.id} className="border-t border-[#2A1B1D]/6" data-testid={`price-row-${s.id}`}>
                  <td className="px-5 py-4 font-medium text-[#2A1B1D]">{s.name}</td>
                  <td className="px-5 py-4 text-[#5C4A4D]">{s.category}</td>
                  <td className="px-5 py-4">
                    <input type="number" value={r.price} onChange={(e) => setField(s.id, "price", e.target.value)} className={cls} data-testid={`price-input-${s.id}`} />
                  </td>
                  <td className="px-5 py-4">
                    <input type="number" value={r.discount_price} onChange={(e) => setField(s.id, "discount_price", e.target.value)} className={cls} placeholder="—" data-testid={`discount-input-${s.id}`} />
                  </td>
                  <td className="px-5 py-4">
                    <button onClick={() => toggleStatus(s)} data-testid={`price-toggle-status-${s.id}`} className={`rounded-full px-3 py-1 text-xs transition-colors ${s.status ? "bg-[#3E664E]/10 text-[#3E664E] hover:bg-[#3E664E]/20" : "bg-[#8E3B46]/10 text-[#8E3B46] hover:bg-[#8E3B46]/20"}`}>
                      {s.status ? "Active" : "Disabled"}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => savePrice(s)}
                      disabled={!changed || saving === s.id}
                      data-testid={`price-save-${s.id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                        changed ? "bg-[#8E3B46] text-[#FDFBF7] hover:bg-[#722F38]" : "bg-[#2A1B1D]/8 text-[#5C4A4D] cursor-default"
                      }`}
                    >
                      <Check size={14} /> {saving === s.id ? "Saving..." : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showHistory && (
        <div className="mt-8" data-testid="price-history-panel">
          <h2 className="font-display text-2xl text-[#2A1B1D] mb-4 flex items-center gap-2"><History size={20} /> Recent Price Changes</h2>
          <div className="rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden">
            {history.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#5C4A4D]">No price changes recorded yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-[#5C4A4D] bg-[#F4EFEA]/60">
                    <th className="px-5 py-3">Service</th>
                    <th className="px-5 py-3">Old Price</th>
                    <th className="px-5 py-3">New Price</th>
                    <th className="px-5 py-3">Changed By</th>
                    <th className="px-5 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} className="border-t border-[#2A1B1D]/6" data-testid={`history-row-${i}`}>
                      <td className="px-5 py-4 font-medium text-[#2A1B1D]">{h.service_name}</td>
                      <td className="px-5 py-4 text-[#5C4A4D] line-through">{inr(h.old_price)}</td>
                      <td className="px-5 py-4 text-[#8E3B46] font-medium">{inr(h.new_price)}</td>
                      <td className="px-5 py-4 text-[#5C4A4D]">{h.changed_by || "—"}</td>
                      <td className="px-5 py-4 text-[#5C4A4D]">{new Date(h.date).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
